import { Injectable } from '@angular/core';
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { environment } from '../../../environments/environment';
import Fernet from 'fernet-web';
import { describePeriod, LicensePeriod, LicensePeriodView } from './license-timeline';

/** `company.id` of this client in the Imaxine license project (MD INDIA) */
export const LICENSE_COMPANY_ID = 1;

/** Decrypted license JSON plus the period it covers */
export interface LicenseInfo extends LicensePeriodView {
    organization?: string;
    license_key?: string;
    contact?: string;
    /** 'expired' = no license covers today; the dates are of the last (or next) one */
    license_status: 'active' | 'expired';
    [key: string]: any;
}

/**
 * Reads this client's licenses from the Imaxine license project. The row
 * dates pick which license applies; validity is then decided from the
 * decrypted license's start_date / end_date (both inclusive).
 */
@Injectable({
    providedIn: 'root'
})
export class LicenseService {
    private supabase: SupabaseClient;
    public licenseData: LicenseInfo | null = null;
    private lastError: string | null = null;

    constructor() {
        this.supabase = createClient(environment.SUPABASE_URL_IMAXINE, environment.SUPABASE_KEY_IMAXINE, {
            auth: { persistSession: false, autoRefreshToken: false },
        });
    }

    /**
     * Every license period of this client, newest first. comment_for_client is
     * only fetched when asked for (the admin license list), never for the banner.
     */
    async fetchCompanyLicensePeriods(withClientComment = false): Promise<LicensePeriod[]> {
        const columns = withClientComment ? 'id, valid_from, valid_to, comment_for_client' : 'id, valid_from, valid_to';
        const { data, error } = await this.supabase
            .from('license')
            .select<string, LicensePeriod>(columns)
            .eq('company_id', LICENSE_COMPANY_ID)
            .order('valid_from', { ascending: false });

        if (error) throw new Error(error.message || 'Could not load licenses');
        return data ?? [];
    }

    /** The license in force today, else the one that ended last, else the next one (decrypted) */
    async loadLicense(): Promise<LicenseInfo> {
        this.lastError = null;
        try {
            const { data, error } = await this.supabase
                .from('license')
                .select('id, valid_from, valid_to, license')
                .eq('company_id', LICENSE_COMPANY_ID);
            if (error) throw new Error(error.message || 'Could not load license');

            const rows = (data ?? []) as (LicensePeriod & { license: string })[];
            const views = rows.map(r => ({ row: r, view: describePeriod(r) }));
            const active = views.filter(v => v.view.state === 'active').sort((a, b) => b.row.valid_to.localeCompare(a.row.valid_to))[0];
            const last = views.filter(v => v.view.state === 'expired').sort((a, b) => b.row.valid_to.localeCompare(a.row.valid_to))[0];
            const next = views.filter(v => v.view.state === 'upcoming').sort((a, b) => a.row.valid_from.localeCompare(b.row.valid_from))[0];
            const chosen = (active ?? last ?? next)?.row;
            if (!chosen) throw new Error('License not found');

            const key = environment.ENCRYPTION_KEY;
            if (!key) throw new Error('Encryption key not found');

            let decoded: any;
            try {
                decoded = JSON.parse(await this.fernetDecrypt(chosen.license, key));
            } catch (e: any) {
                throw new Error('Failed to decrypt license: ' + (e?.message || e));
            }

            // Dates inside the signed license win over the table columns
            const period = describePeriod({
                id: chosen.id,
                valid_from: decoded.start_date || chosen.valid_from,
                valid_to: decoded.end_date || chosen.valid_to,
            });
            this.licenseData = { ...decoded, ...period, license_status: period.state === 'active' ? 'active' : 'expired' };
            return this.licenseData!;
        } catch (e: any) {
            console.error('License load failed:', e);
            this.licenseData = null;
            this.lastError = e?.message || String(e);
            throw new Error(this.lastError!);
        }
    }

    private async fernetDecrypt(token: string, keyB64Url: string): Promise<string> {
        if (!globalThis.crypto?.subtle) {
            const protocol = globalThis.location?.protocol || 'unknown protocol';
            throw new Error(
                'Web Crypto is not available on this origin (' + protocol + '). Serve the production app over HTTPS because license decryption requires crypto.subtle.'
            );
        }
        const fernet = await Fernet.create(keyB64Url);
        return await fernet.decrypt(token);
    }

    async isValid(): Promise<boolean> {
        // Dev escape hatch; never honoured in a production build
        if (!(environment as { production?: boolean }).production && (environment as { SKIP_LICENSE_CHECK?: boolean }).SKIP_LICENSE_CHECK) {
            console.warn('Licence check skipped: SKIP_LICENSE_CHECK is set in a non-production environment.');
            return true;
        }

        // Re-check once the cached license stops covering today (e.g. a tab left open past expiry)
        if (!this.licenseData || describePeriod(this.licenseData).state !== 'active') {
            try {
                await this.loadLicense();
            } catch {
                return false;
            }
        }
        return describePeriod(this.licenseData!).state === 'active';
    }

    async getInfo(): Promise<LicenseInfo | null> {
        if (!this.licenseData) await this.loadLicense();
        return this.licenseData;
    }

    getLastError(): string | null {
        return this.lastError;
    }
}
